<template>
    <DashboardLayout :user="user">

        <div class="border border-gray-300 bg-white rounded-lg shadow-lg max-w-4xl mx-auto pb-10">

            <div class="bg-gray-200 text-lg font-semibold px-6 py-3 rounded-t-lg text-left">
                Loan Repayment
            </div>

            <div class="max-w-3xl mx-auto bg-white border border-gray-300 rounded-lg shadow-lg px-6 py-4 mt-5">
                <div class="flex flex-col items-center">
                    <div class="flex items-center mb-4 pb-2">
                        <label class="min-w-[150px] text-gray-700 font-bold text-right pr-4">Account Number:</label>
                        <div class="font-bold rounded px-3 py-2">{{ loanDetails.cbsac }}</div>
                    </div>
                    <div class="flex items-center mb-4 pb-2">
                        <label class="min-w-[150px] text-gray-700 font-bold text-right pr-4">Outstanding
                            Balance:</label>
                        <div class="font-bold rounded px-3 py-2">₹{{ loanStatements.total_amount }}</div>
                    </div>
                </div>

                <!-- Select Bank Account -->
                <div class="mb-4">
                    <label class="block text-sm font-medium mb-1">Select Bank Account:</label>
                    <select v-model="selectedKhate" :disabled="formLocked" class="w-3/4 border rounded px-3 py-2">
                        <option disabled value="">Choose an account</option>
                        <option v-for="(account, i) in loanBalance.balances" :key="i" :value="account.khate">
                            {{ account.account_name }} - ₹{{ account.balance }}
                        </option>
                    </select>
                </div>

                <!-- Enter Amount -->
                <div class="mb-4">
                    <label class="block text-sm font-medium mb-1">Enter Amount:</label>
                    <input v-model.number="repaymentAmount" :disabled="formLocked" type="number"
                        placeholder="Enter repayment amount" class="w-3/4 border rounded px-3 py-2" />
                </div>

                <p v-if="repaymentAmount > maxAllowedAmount" class="text-red-500 text-sm mt-1">
                    Amount cannot be greater than ₹{{ maxAllowedAmount }}
                </p>

                <div v-if="showAuthField" class="mb-4">
                    <label class="block text-sm font-medium mb-1">
                        {{ repaymentAmount < 25000 ? 'Enter Password:' : 'Enter OTP:' }} </label>

                            <input v-if="repaymentAmount < 25000" v-model="authValue" type="password"
                                class="w-3/4 border rounded px-3 py-2" placeholder="Enter your password" />

                            <!-- Use your OTP component -->
                            <div v-else class="border rounded px-3 py-2 inline-block">
                                <OtpInput :otpLength="6" class="w-full justify-center" @updateOtp="authValue = $event" />

                            </div>

                </div>

                <button v-if="showAuthField" @click="submitFinalPayment"
                    class="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded">
                    Submit Final Payment
                </button>

                <!-- Submit Button -->
                <button v-else @click="submitRepayment"
                    class="bg-primary text-white px-6 py-2 rounded hover:bg-blue-700">
                    Submit Amount
                </button>

                <p v-if="errorMessages" class="text-red-500 mt-2">{{ errorMessages }}</p>
                <p v-if="successMessage" class="text-green-500 mt-2">{{ successMessage }}</p>

            </div>
        </div>

        <!-- Payment Confirmation Modal -->
        <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div class="bg-white p-6 rounded-lg shadow-xl text-center max-w-md w-full">
                <h2 class="text-xl font-semibold text-gray-800 mb-4">Please confirm the amount and the account number
                    from which the amount will be deducted.</h2>

                <div class="text-left mb-4 space-y-2">
                    <p><strong>Amount:</strong> ₹{{ amount }}</p>
                    <p><strong>Order ID:</strong> {{ confirmedOrderId }}</p>
                </div>

                <div class="flex justify-center space-x-4">
                    <button @click="showModal = false"
                        class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded">
                        Cancel
                    </button>
                    <button @click="confirmPayment" class="bg-primary hover:bg-green-700 text-white px-4 py-2 rounded">
                        Confirm
                    </button>
                    <p v-if="errorMessages" class="text-red-500 mt-2">{{ errorMessages }}</p>
                </div>
            </div>
        </div>



    </DashboardLayout>

</template>

<script>


import { apiRequest } from "@/api";
import DashboardLayout from "@/components/DashboardLayout.vue";
import OtpInput from "@/components/OtpInput.vue";

export default {
    name: 'LoanRepayment',
    components: {
        DashboardLayout, OtpInput
    },
    data() {
        return {
            user: {},
            loanDetails: null,
            loanStatements: null,
            loanBalance: null,
            errorMessages: '',
            selectedKhate: '',
            repaymentAmount: '',
            showModal: false,
            amount: '',
            confirmedOrderId: '',
            showAuthField: false,
            authValue: '',
            formLocked: false,
            verificationType: '',
            successMessage: ''
        };
    },
    computed: {
        selectedAccount() {
            return this.loanBalance?.balances?.find(
                acc => acc.khate === this.selectedKhate
            ) || null;
        },
        maxAllowedAmount() {
            return parseFloat(this.loanStatements.total_amount) || 0;
        },
        amountInPaise() {
            return Math.round(this.repaymentAmount * 100);
        }
    },
    created() {
        const userData = localStorage.getItem('user');

        if (!userData) {
            alert("Session expired. Please log in again.");
            this.$router.push("/");
            return;
        }
        this.user = JSON.parse(userData);

        this.loanDetails = JSON.parse(sessionStorage.getItem("selectedLoan")) || {};
        this.loanStatements = JSON.parse(sessionStorage.getItem("bankStatement")) || {};
        this.loanBalance = JSON.parse(sessionStorage.getItem("balances")) || {};
    },
    methods: {
        async submitFinalPayment() {
            try {
                if (!this.authValue || (this.verificationType === 'otp' && this.authValue.length !== 6)) {
                    this.showError("Please enter the valid OTP or password.");
                    return;
                }
                const response = await apiRequest('/verify_order', 'POST', {
                    phone: this.user.mobile,
                    order_id: this.confirmedOrderId,
                    verification_type: this.verificationType,
                    otp_or_password: this.authValue
                });

                if (response.data.status === 'success') {
                    await this.submitOrder();
                } else {
                    this.showError(response.data.message);
                }

            } catch (error) {
                this.showError("Something went wrong during payment submission.");
            }
        },
        async confirmPayment() {
            this.showModal = false;
            this.showAuthField = true;
            this.formLocked = true;
        },
        async submitOrder() {
            try {
                const response = await apiRequest('/submit_order', 'POST', {
                    orderId: this.confirmedOrderId,
                    phone: this.user.mobile
                });

                if (response.data.status === 'success') {
                    this.successMessage = "Transaction successfully submitted.";
                    // Optionally check payment status too
                    await this.confirmOrder(); // still keep this
                } else {
                    this.showError(response.data.message || "Submit Order failed.");
                }
            } catch (err) {
                this.showError("Transaction submission failed. Try again.");
            }
        },
        async confirmOrder() {
            try {
                const response = await apiRequest('/payment_status', 'POST', {
                    phone: this.user.mobile,
                    orderId: this.confirmedOrderId
                });

                if (response.data.status === 'verified') {
                    this.successMessage = "Payment Successful."
                    setTimeout(() => {
                        this.$router.push('/loans');
                    }, 3000);
                } else {
                    this.showError(response.data.message);
                }

            } catch (error) {
                console.error("Error:", error);
            }
        },
        async submitRepayment() {

            if (!this.repaymentAmount || !this.selectedAccount) {
                this.showError("Please select a bank account and enter an amount.");
                return;
            }

            if (this.repaymentAmount > this.maxAllowedAmount) {
                this.showError(`Amount cannot be greater than ₹${this.maxAllowedAmount}`);
                return;
            }


            try {
                const response = await apiRequest('/order', 'POST', {
                    acno: this.user.acno,
                    amount: this.amountInPaise,
                    phone: this.user.mobile,
                    khate: this.loanDetails.khate,
                    type: this.loanDetails.sub_type,
                    table: this.loanDetails.table,
                    cbsac: '0',
                    payment_id: null,
                    from_khate: this.selectedAccount.khate,
                    from_type: this.selectedAccount.account_type

                });

                if (response.data.status === 'verify') {
                    this.confirmedOrderId = response.data.order_id;
                    this.verificationType = response.data.verificationType;
                    this.amount = this.repaymentAmount;
                    this.showModal = true;
                } else {
                    this.showError(response.data.message);
                }

            } catch (error) {
                console.error("Error:", error);
            }
        },
        moveToNext(index, event) {
            if (event.target.value && index < 5) {
                this.$refs.otpInputs[index + 1].focus();
            }
        },
        moveToPrev(index, event) {
            if (!event.target.value && index > 0) {
                this.$refs.otpInputs[index - 1].focus();
            }
        },
        showError(message) {
            this.errorMessages = message;
            if (this.errorTimeout) {
                clearTimeout(this.errorTimeout);
            }
            this.errorTimeout = setTimeout(() => {
                this.errorMessages = '';
            }, 3000);
        },
    }
}

</script>

<style scoped>
.otp-box {
    width: 45px;
    height: 45px;
    font-size: 18px;
    text-align: center;
    border: 1px solid #ccc;
    border-radius: 4px;
    outline: none;
}

.password-strength {
    height: 5px;
    background-color: #ddd;
    border-radius: 4px;
    margin-top: 5px;
    overflow: hidden;
}

.password-strength span {
    display: block;
    height: 100%;
    transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out;
}
</style>
